import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import AdUnit from '../components/AdUnit';
import Footer from '../components/Footer';
import SEO from '../components/SEO';

// Get site domain from environment variables
const SITE_DOMAIN = process.env.REACT_APP_SITE_DOMAIN || 'https://yourjsontools.com';

const DummyFilesPage = () => {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "Dummy Files Generator",
    "url": `${SITE_DOMAIN}/dummy-files`,
    "description": "Generate dummy image and PDF files for testing purposes. Create placeholder images in various sizes and sample PDF documents.",
    "applicationCategory": "DeveloperApplication",
    "operatingSystem": "Web",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    }
  };

  return (
    <>
      <SEO 
        title="Dummy Files Generator - Create Test Images & PDFs | yourjsontools.com"
        description="Generate dummy image and PDF files for testing. Create placeholder images in various sizes and sample PDF documents with different page counts."
        keywords={["dummy files", "test images", "placeholder images", "sample PDF", "dummy PDF", "file generator", "testing tools"]}
        canonicalUrl={`${SITE_DOMAIN}/dummy-files`}
        structuredData={jsonLd}
      />

      <div className="page-container">
        <div className="page-header">
          <h1>Dummy Files Generator</h1>
          <p className="page-description">
            Generate dummy files for testing purposes. Perfect for developers, designers, and testers who need placeholder content for their projects.
          </p>
        </div>

        <div className="tool-section">
          <div className="file-types-grid">
            <Link to="/dummy-files/images" className="file-type-card">
              <div className="file-type-icon">🖼️</div>
              <h3>Dummy Images</h3>
              <p>Generate placeholder images in various sizes</p>
              <div className="features-list">
                <span>• 8 different sizes</span>
                <span>• PNG, JPEG & WebP formats</span>
                <span>• 100x100 to 1920x1080</span>
                <span>• Instant download</span>
              </div>
              <div className="cta-button">
                Generate Images →
              </div>
            </Link>

            <Link to="/dummy-files/pdf" className="file-type-card">
              <div className="file-type-icon">📄</div>
              <h3>Dummy PDFs</h3>
              <p>Generate sample PDF documents</p>
              <div className="features-list">
                <span>• 1 to 50 pages</span>
                <span>• 10KB to 500KB sizes</span>
                <span>• Real PDF format</span>
                <span>• Lorem ipsum content</span>
              </div>
              <div className="cta-button">
                Generate PDFs →
              </div>
            </Link>
          </div>
        </div>

        <div className="info-section">
          <h2>Why Use Dummy Files?</h2>
          <div className="info-grid">
            <div className="info-card">
              <h3>🧪 Testing & Development</h3>
              <p>
                Test file upload functionality, image processing systems, and document workflows 
                with realistic dummy files in various formats and sizes.
              </p>
            </div>
            <div className="info-card">
              <h3>🎨 Design & Mockups</h3>
              <p>
                Use placeholder images for design mockups, wireframes, and prototypes. 
                Perfect for when you need content but don't have real assets yet.
              </p>
            </div>
            <div className="info-card">
              <h3>📊 Performance Testing</h3>
              <p>
                Test how your applications handle different file sizes and formats. 
                Useful for load testing and storage capacity planning.
              </p>
            </div>
            <div className="info-card">
              <h3>🔒 Privacy & Security</h3>
              <p>
                All files are generated locally in your browser. No real data is used, 
                ensuring privacy and security during testing phases.
              </p>
            </div>
          </div>
        </div>

        <div className="features-section">
          <h2>Key Features</h2>
          <div className="features-grid">
            <div className="feature-item">
              <div className="feature-icon">⚡</div>
              <div className="feature-content">
                <h4>Instant Generation</h4>
                <p>Files are created and downloaded immediately with no waiting time.</p>
              </div>
            </div>
            <div className="feature-item">
              <div className="feature-icon">🌐</div>
              <div className="feature-content">
                <h4>Browser-Based</h4>
                <p>No software installation required. Works entirely in your web browser.</p>
              </div>
            </div>
            <div className="feature-item">
              <div className="feature-icon">📱</div>
              <div className="feature-content">
                <h4>Mobile Friendly</h4>
                <p>Responsive design works perfectly on desktop, tablet, and mobile devices.</p>
              </div>
            </div>
            <div className="feature-item">
              <div className="feature-icon">🆓</div>
              <div className="feature-content">
                <h4>Completely Free</h4>
                <p>No registration, no limits, no hidden costs. Generate as many files as you need.</p>
              </div>
            </div>
          </div>
        </div>

      </div>

      <Footer />

      <style jsx>{`
        .file-types-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
          gap: 2rem;
          max-width: 800px;
          margin: 0 auto 3rem;
        }

        .file-type-card {
          background: white;
          border-radius: 16px;
          padding: 2.5rem;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
          transition: all 0.3s ease;
          text-decoration: none;
          color: inherit;
          display: block;
          text-align: center;
          border: 2px solid transparent;
        }

        .file-type-card:hover {
          transform: translateY(-8px);
          box-shadow: 0 12px 30px rgba(0, 0, 0, 0.15);
          border-color: #3498db;
        }

        .file-type-icon {
          font-size: 4rem;
          margin-bottom: 1.5rem;
        }

        .file-type-card h3 {
          margin: 0 0 1rem 0;
          color: #2c3e50;
          font-size: 1.5rem;
        }

        .file-type-card p {
          margin: 0 0 1.5rem 0;
          color: #7f8c8d;
          font-size: 1rem;
        }

        .features-list {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
          margin-bottom: 2rem;
          text-align: left;
        }

        .features-list span {
          color: #555;
          font-size: 0.9rem;
        }

        .cta-button {
          background: linear-gradient(135deg, #3498db, #2980b9);
          color: white;
          padding: 1rem 2rem;
          border-radius: 8px;
          font-weight: 600;
          font-size: 1rem;
          transition: all 0.2s ease;
        }

        .file-type-card:hover .cta-button {
          background: linear-gradient(135deg, #2980b9, #1f5f8b);
          transform: scale(1.05);
        }

        .info-section, .features-section {
          margin-top: 4rem;
        }

        .info-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 2rem;
          margin-top: 2rem;
        }

        .info-card {
          background: white;
          padding: 2rem;
          border-radius: 12px;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
          transition: transform 0.2s ease;
        }

        .info-card:hover {
          transform: translateY(-2px);
        }

        .info-card h3 {
          margin: 0 0 1rem 0;
          color: #2c3e50;
          font-size: 1.2rem;
        }

        .info-card p {
          margin: 0;
          color: #7f8c8d;
          line-height: 1.6;
        }

        .features-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 1.5rem;
          margin-top: 2rem;
        }

        .feature-item {
          display: flex;
          align-items: flex-start;
          gap: 1rem;
          padding: 1.5rem;
          background: white;
          border-radius: 8px;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }

        .feature-icon {
          font-size: 2rem;
          flex-shrink: 0;
        }

        .feature-content h4 {
          margin: 0 0 0.5rem 0;
          color: #2c3e50;
          font-size: 1.1rem;
        }

        .feature-content p {
          margin: 0;
          color: #7f8c8d;
          font-size: 0.9rem;
          line-height: 1.5;
        }

        @media (max-width: 768px) {
          .file-types-grid {
            grid-template-columns: 1fr;
            gap: 1.5rem;
          }

          .file-type-card {
            padding: 2rem;
          }

          .features-grid {
            grid-template-columns: 1fr;
          }

          .feature-item {
            padding: 1rem;
          }
        }
      `}</style>
    </>
  );
};

export default DummyFilesPage; 